import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../api/axios";
import "./Notifications.css";
import { Link } from "react-router-dom";
import { getSocket } from "../../../socket/Socket";
import ProfileBadge from "../../../assets/images/badge/Bhagwadhar.png";
import { toast } from "react-toastify";
import { MdDelete } from "react-icons/md";
import DeleteAllNotification from "../../model/DeleteAllNotification";
import { MdDeleteSweep } from "react-icons/md";

const INITIAL_VISIBLE = 5;  // How many to show before "See More"
const LIMIT = 10;           // How many notifications to fetch per API call (batch size)

export default function Notification({ onClose }) {
  const navigate = useNavigate();
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Cursor-based pagination states
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Refs to always hold latest cursor/hasMore — avoids stale closure in scroll handler
  const cursorRef = useRef(null);
  const hasMoreRef = useRef(true);
  const seeMoreClickedRef = useRef(false);

  // "See More" feature states
  const [seeMoreClicked, setSeeMoreClicked] = useState(false);
  // True only when API explicitly returns hasMore: false — all notifications loaded
  const [isExhausted, setIsExhausted] = useState(false);

  const notificationsContainerRef = useRef(null);
  const isFetchingRef = useRef(false);
  const sentinelRef = useRef(null); // Invisible div at bottom — triggers next fetch when visible

  /* ================= DELETE HANDLERS ================= */

  const deleteNotification = async (notificationId) => {
    try {
      await api.delete(`/notifications/delete/${notificationId}`);
      toast.success("Notification deleted successfully");
      setNotifications((prev) =>
        prev.filter((n) => n._id?.toString() !== notificationId?.toString())
      );
    } catch (error) {
      console.log("Failed to delete notification", error);
      toast.error("Failed to delete notification");
    }
  };

  const deleteAllNotification = async () => {
    try {
      await api.delete("/notifications/Alldelete");
      toast.success("All notification deleted successfully");
      setNotifications([]);
      setCursor(null);
      setHasMore(false);
      setSeeMoreClicked(false);
      seeMoreClickedRef.current = false;
      cursorRef.current = null;
      hasMoreRef.current = true;
      setIsExhausted(false);
    } catch (error) {
      console.log("Failed to delete all notification", error);
      toast.error("Failed to delete all notification");
    }
  };

  /* ================= SOCKET.IO ================= */

  const addNotificationToTop = useCallback((notification) => {
    setNotifications((prev) => [notification, ...prev]);
  }, []);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    socket.on("followed", (data) => {
      addNotificationToTop({
        _id: data?.requestId || Date.now(),
        type: "FOLLOWED",
        createdAt: new Date(),
        isRead: false,
        from: {
          _id: data?.from?._id,
          fullName: data?.from?.fullName,
          profilePic: data?.from?.profilePic,
          isBatch: data?.from?.isBatch,
        },
        referenceId: data?.postId,
      });
    });

    socket.on("follow_request", (data) => {
      addNotificationToTop({
        _id: data?.requestId || Date.now(),
        type: "FOLLOW_REQUEST",
        createdAt: new Date(),
        isRead: false,
        from: {
          _id: data?.from?._id,
          fullName: data?.from?.fullName,
          profilePic: data?.from?.profilePic,
          isBatch: data?.from?.isBatch,
        },
        reference: { _id: data?.requestId, status: "PENDING" },
        referenceId: data?.postId,
      });
    });

    socket.on("follow_request_accepted", (data) => {
      addNotificationToTop({
        _id: data?.requestId || Date.now(),
        type: "FOLLOW_ACCEPTED",
        createdAt: new Date(),
        isRead: false,
        from: {
          _id: data?.from?._id,
          fullName: data?.from?.fullName,
          profilePic: data?.from?.profilePic,
          isBatch: data?.from?.isBatch,
        },
        reference: { _id: data?.requestId, status: "ACCEPTED" },
        referenceId: data?.postId,
      });
    });

    socket.on("post_liked", (data) => {
      addNotificationToTop({
        _id: data?.requestId || Date.now(),
        type: "POST_LIKED",
        createdAt: new Date(),
        isRead: false,
        from: {
          _id: data?.from?._id,
          fullName: data?.from?.fullName,
          profilePic: data?.from?.profilePic,
          isBatch: data?.from?.isBatch,
        },
        reference: { _id: data?.postId },
        referenceId: data?.postId,
      });
    });

    socket.on("comment_replied", (data) => {
      addNotificationToTop({
        _id: data?.requestId || Date.now(),
        type: "COMMENT_REPLIED",
        createdAt: new Date(),
        isRead: false,
        from: {
          _id: data?.from?._id,
          fullName: data?.from?.fullName,
          profilePic: data?.from?.profilePic,
          isBatch: data?.from?.isBatch,
        },
        reference: { _id: data?.commentId, post: data?.postId },
        referenceId: data?.postId,
      });
    });

    socket.on("post_commented", (data) => {
      addNotificationToTop({
        _id: data?.requestId || Date.now(),
        type: "POST_COMMENTED",
        createdAt: new Date(),
        isRead: false,
        from: {
          _id: data?.from?._id,
          fullName: data?.from?.fullName,
          profilePic: data?.from?.profilePic,
          isBatch: data?.from?.isBatch,
        },
        reference: { _id: data?.commentId, post: data?.postId },
        referenceId: data?.postId,
      });
    });

    socket.on("post_reposted", (data) => {
      addNotificationToTop({
        _id: data?.requestId || Date.now(),
        type: "POST_REPOSTED",
        createdAt: new Date(),
        isRead: false,
        from: {
          _id: data?.from?._id,
          fullName: data?.from?.fullName,
          profilePic: data?.from?.profilePic,
          isBatch: data?.from?.isBatch,
        },
        reference: { _id: data?.postId },
        referenceId: data?.postId,
      });
    });

    socket.on("revolution_supported", (data) => {
      addNotificationToTop({
        _id: data?.requestId || Date.now(),
        type: "SUPPORTED_REVOLUTION",
        createdAt: new Date(),
        isRead: false,
        from: {
          _id: data?.from?._id,
          fullName: data?.from?.fullName,
          profilePic: data?.from?.profilePic,
          isBatch: data?.from?.isBatch,
        },
        referenceId: data?.revolutionId,
      });
    });

    socket.on("new_message_request", (data) => {
      addNotificationToTop({
        _id: data?.requestId || Date.now(),
        type: "MESSAGE_REQUEST_SENT",
        createdAt: new Date(),
        isRead: false,
        from: {
          _id: data?.from?._id,
          fullName: data?.from?.fullName,
          profilePic: data?.from?.profilePic,
          isBatch: data?.from?.isBatch,
        },
      });
    });

    socket.on("message_request_accepted", (data) => {
      addNotificationToTop({
        _id: data?.requestId || Date.now(),
        type: "MESSAGE_REQUEST_ACCEPTED",
        createdAt: new Date(),
        isRead: false,
        from: {
          _id: data?.from?._id,
          fullName: data?.from?.fullName,
          profilePic: data?.from?.profilePic,
          isBatch: data?.from?.isBatch,
        },
      });
    });

    socket.on("comment_liked", (data) => {
      addNotificationToTop({
        _id: data?.requestId || Date.now(),
        type: "COMMENT_LIKED",
        createdAt: new Date(),
        isRead: false,
        from: {
          _id: data?.from?._id,
          fullName: data?.from?.fullName,
          profilePic: data?.from?.profilePic,
          isBatch: data?.from?.isBatch,
        },
        reference: { _id: data?.commentId, post: data?.postId },
        referenceId: data?.postId,
      });
    });

    return () => {
      socket.off("followed");
      socket.off("follow_request");
      socket.off("follow_request_accepted");
      socket.off("post_liked");
      socket.off("comment_replied");
      socket.off("post_commented");
      socket.off("post_reposted");
      socket.off("revolution_supported");
      socket.off("new_message_request");
      socket.off("message_request_accepted");
      socket.off("comment_liked");
    };
  }, [addNotificationToTop]);

  /* ================= FETCH ================= */

  const fetchNotifications = useCallback(async (cursorParam = null, isLoadMore = false) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    isLoadMore ? setLoadingMore(true) : setLoading(true);

    try {
      const response = await api.get("/notifications/", {
        params: {
          limit: LIMIT,
          ...(cursorParam && { cursor: cursorParam }),
        },
      });

      const newNotifications = response.data.notifications || [];
      const nextCursor = response.data.nextCursor || null;
      const hasMoreData =
        response.data.hasMore !== undefined
          ? response.data.hasMore
          : !!(nextCursor && newNotifications.length > 0);

      if (isLoadMore) {
        setNotifications((prev) => {
          return [...prev, ...newNotifications];
        });
      } else {
        setNotifications(newNotifications);
      }

      setCursor(nextCursor);
      setHasMore(hasMoreData);
      cursorRef.current = nextCursor;
      hasMoreRef.current = hasMoreData;
      setIsInitialLoad(false);
      if (!hasMoreData) {
        setIsExhausted(true);
      }
    } catch (error) {
      console.log("Something went wrong", error);
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isFetchingRef.current = false;
    }
  }, []);

  // Initial load on mount
  useEffect(() => {
    fetchNotifications(null, false);
  }, [fetchNotifications]);

  /* ================= INFINITE SCROLL (IntersectionObserver) ================= */
  // Re-runs whenever notifications list renders or seeMoreClicked changes
  // so sentinelRef.current is guaranteed to exist in the DOM when observed.

  useEffect(() => {
    // sentinel only exists in DOM after notifications are loaded
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (
          entry.isIntersecting &&
          seeMoreClickedRef.current &&
          hasMoreRef.current &&
          !isFetchingRef.current &&
          cursorRef.current
        ) {
          fetchNotifications(cursorRef.current, true);
        }
      },
      {
        // Watch intersection inside the scrollable container, not the viewport
        root: notificationsContainerRef.current,
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchNotifications, seeMoreClicked, notifications.length]);

  /* ================= ACTION HANDLERS ================= */

  const handleFollowBack = async (userId, notificationId) => {
    try {
      await api.post(`/follows/${userId}/follow`);
      toast.success("You have followed back");
      setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
    } catch (error) {
      console.log("Follow back failed", error);
      toast.error("Failed to follow back");
    }
  };

  const [isStatusChanging, setIsStatusChanging] = useState(false);

  const handleAcceptRequest = async (userID, notificationId) => {
    try {
      const { data } = await api.post(`/follows/accept/${notificationId}`);
      setIsStatusChanging(true);
      toast.success("You are now friends");
      setNotifications((prev) =>
        prev.map((n) =>
          n._id?.toString() === notificationId?.toString()
            ? {
                ...n,
                type: "FOLLOWED",
                reference: {
                  _id: data?.follow?._id || null,
                  status: "ACCEPTED",
                },
              }
            : n
        )
      );
    } catch (error) {
      setIsStatusChanging(false);
      console.log("Accept failed", error);
      toast.error("Failed to accept request");
    }
  };

  const handleCancelRequest = async (userId, notificationId) => {
    try {
      await api.post(`/follows/reject/${notificationId}`);
      setNotifications((prev) =>
        prev.filter((n) => n._id?.toString() !== notificationId?.toString())
      );
      toast.success("Request cancelled");
    } catch (error) {
      console.log("Reject failed", error);
      toast.error("Failed to cancel request");
    }
  };

  const stop = (e) => e.stopPropagation();

  /* ================= HELPERS ================= */

  const timeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;
    return `${Math.floor(days / 7)} week${Math.floor(days / 7) > 1 ? "s" : ""} ago`;
  };

  const SYSTEM_NOTIFICATION_TYPES = new Set([
    "KYC_VERIFIED",
    "KYC_REJECTED",
    "REVOLUTION_ACCEPTED",
    "REVOLUTION_REJECTED",
  ]);

  const isSystemNotification = (type) =>
    SYSTEM_NOTIFICATION_TYPES.has(type?.toString().toUpperCase().trim());

  const getNotificationMessage = (type) => {
    const normalizedType = type?.toString().toUpperCase().trim();
    const messages = {
      FOLLOWED: "followed you",
      FOLLOW: "followed you",
      FOLLOW_REQUEST: "sent you a follow request",
      FOLLOW_ACCEPTED: "accepted your follow request",
      FOLLOW_REQUEST_ACCEPTED: "accepted your follow request",
      FOLLOW_REJECTED: "rejected your follow request",
      POST_LIKED: "liked your post",
      LIKED: "liked your post",
      POST_COMMENTED: "commented on your post",
      COMMENTED: "commented on your post",
      POST_REPOSTED: "reposted your post",
      REPOSTED: "reposted your post",
      SUPPORTED_REVOLUTION: "supported your revolution",
      MESSAGE_REQUEST_SENT: "sent you a message request",
      MESSAGE_REQUEST_ACCEPTED: "accepted your message request",
      COMMENT_REPLIED: "replied to your comment",
      COMMENT_LIKED: "liked your comment",
      KYC_VERIFIED: "Your KYC has been verified successfully ✅",
      KYC_REJECTED: "Your KYC verification was rejected ❌",
      REVOLUTION_ACCEPTED: "Your revolution has been accepted ✅",
      REVOLUTION_REJECTED: "Your revolution was rejected ❌",
    };

    if (messages[normalizedType]) return messages[normalizedType];
    console.warn(`[Notification] Unhandled notification type: "${type}"`);
    return "You have a new notification";
  };

  /* ================= NOTIFICATION CLICK ================= */

  const handleNotificationClick = async (item) => {
    if (!item.reference && !item.referenceId) return;

    if (item.type === "POST_LIKED") {
      const postId = item.reference?._id || item.referenceId;
      navigate(`/p/${postId}`, { state: { fromLikeNotification: true } });
      if (onClose) onClose();
      return;
    }
    if (item.type === "POST_COMMENTED") {
      const postId = item?.reference?.post || item?.referenceId;
      const commentId = item?.reference?._id || item?.referenceId;
      navigate(`/p/${postId}`, { state: { commentId, fromNotification: true } });
      if (onClose) onClose();
      return;
    }
    if (item.type === "COMMENT_LIKED") {
      const postId = item?.reference?.post || item?.referenceId;
      const commentId = item?.reference?._id || item?.referenceId;
      navigate(`/p/${postId}`, { state: { commentId, fromNotification: true } });
      if (onClose) onClose();
      return;
    }
    if (item.type === "COMMENT_REPLIED") {
      const postId = item?.reference?.post || item?.referenceId;
      const commentId = item?.reference?._id || item?.referenceId;
      navigate(`/p/${postId}`, { state: { commentId, fromNotification: true } });
      if (onClose) onClose();
      return;
    }
    if (item.type === "POST_REPOSTED") {
      const postId =
        item.reference?.post ||
        item.reference?._id ||
        item.referenceId ||
        item.postId;
      navigate(`/p/${postId}`, { state: { fromNotification: true } });
      if (onClose) onClose();
      return;
    }
  };

  /* ================= UI HELPERS ================= */

  const deleteAllPopup = (e) => {
    e.stopPropagation();
    setShowDeletePopup(true);
  };

  const NotificationSkeleton = () => (
    <div className="notification_row skeleton">
      <div className="notification_item">
        <div className="notification_container_details">
          <div className="skeleton-avatar"></div>
          <div className="content">
            <div className="skeleton-line"></div>
            <div className="skeleton-line short"></div>
          </div>
        </div>
      </div>
    </div>
  );

  // Decide which notifications to render:
  // - Before "See More" click: only first INITIAL_VISIBLE (5)
  // - After "See More" click: all fetched notifications
  const visibleNotifications = seeMoreClicked
    ? notifications
    : notifications.slice(0, INITIAL_VISIBLE);

  // Show "See More" button only if not clicked yet AND there are more than INITIAL_VISIBLE notifications
  // OR if there is hasMore data from the API (meaning even more exist beyond the fetched batch)
  const showSeeMoreButton =
    !seeMoreClicked &&
    (notifications.length > INITIAL_VISIBLE || hasMore);

  /* ================= RENDER ================= */

  return (
    <>
      <section
        className="notification_wrapper"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="notification_container" ref={notificationsContainerRef}>
          <div className="notification_title_section">
            <h3 className="notification_title">Notifications</h3>
            {notifications.length > 0 && (
              <MdDeleteSweep onClick={(e) => deleteAllPopup(e)} />
            )}
          </div>

          {/* Initial Loading Skeletons */}
          {loading && isInitialLoad && (
            <div className="notification-loading">
              {[...Array(5)].map((_, index) => (
                <NotificationSkeleton key={index} />
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && notifications.length === 0 && (
            <div className="notification-notfound">
              <h2>No Notifications</h2>
              <p>When you get notifications, they'll appear here</p>
            </div>
          )}

          {/* Notifications List */}
          {!loading && notifications.length > 0 && (
            <>
              {visibleNotifications.map((item, index) => {
                const isSystem = isSystemNotification(item?.type);

                if (!isSystem && (!item?.from?.fullName || !item?.type)) {
                  console.warn(
                    "[Notification] Incomplete user notification data:",
                    item
                  );
                }

                return (
                  <div key={item._id || index} className="notification_row">
                    <div className="notification_item">
                      <div className="notification_container_details">

                        {isSystem ? (
                          <div className="notification-profile-container">
                            <img
                              src="https://cdn-icons-png.flaticon.com/512/1827/1827301.png"
                              alt="System"
                              className="profile_pic"
                            />
                          </div>
                        ) : (
                          <Link
                            to={`/user/${item?.from?._id}/profile`}
                            onClick={onClose}
                          >
                            <div className="notification-profile-container">
                              <img
                                src={
                                  item?.from?.profilePic ||
                                  "https://cdn-icons-png.flaticon.com/512/149/149071.png"
                                }
                                alt={item?.from?.fullName || "User"}
                                className="profile_pic"
                                onError={(e) => {
                                  e.target.src =
                                    "https://cdn-icons-png.flaticon.com/512/149/149071.png";
                                }}
                              />
                              {item?.from?.isBatch && (
                                <div className="notification_profile_badge_section">
                                  <img src={ProfileBadge} alt="profile-badge" />
                                </div>
                              )}
                            </div>
                          </Link>
                        )}

                        <div
                          className="content"
                          onClick={() => handleNotificationClick(item)}
                        >
                          <p>
                            {isSystem ? (
                              getNotificationMessage(item?.type)
                            ) : (
                              <>
                                <strong>
                                  {item?.from?.fullName || "Someone"}
                                </strong>{" "}
                                {getNotificationMessage(item?.type)}
                              </>
                            )}
                          </p>
                          <small>{timeAgo(item?.createdAt)}</small>
                        </div>
                      </div>

                      <div className="notification-follow-btn">
                        {item.type === "FOLLOW_REQUEST" && (
                          <>
                            <button
                              className="follow_back_btn"
                              onClick={(e) => {
                                stop(e);
                                handleAcceptRequest(
                                  item?.from?._id,
                                  item?.reference?._id || item?._id
                                );
                              }}
                              disabled={isStatusChanging}
                            >
                              Accept
                            </button>
                            <button
                              className="cancel_btn"
                              onClick={(e) => {
                                stop(e);
                                handleCancelRequest(
                                  item?.from?._id,
                                  item?.reference?._id || item?._id
                                );
                              }}
                            >
                              Cancel
                            </button>
                          </>
                        )}

                        {item.type === "FOLLOW_ACCEPTED" &&
                          item.reference?.status === "ACCEPTED" && (
                            <button
                              className="following_btn"
                              onClick={(e) => {
                                stop(e);
                                handleFollowBack(item?.from?._id, item?._id);
                              }}
                            >
                              Follow Back
                            </button>
                          )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* See More Button */}
              {showSeeMoreButton && (
                <div className="see-more-notifications">
                  <button
                    className="see_more_btn"
                    onClick={() => { setSeeMoreClicked(true); seeMoreClickedRef.current = true; }}
                  >
                    See More
                  </button>
                </div>
              )}

              {/* Loading More Skeletons (only visible after See More is clicked) */}
              {seeMoreClicked && loadingMore && (
                <div className="loading-more-notifications">
                  {[...Array(3)].map((_, index) => (
                    <NotificationSkeleton key={`more-${index}`} />
                  ))}
                </div>
              )}

              {/* All caught up message — only after See More clicked AND API confirmed no more pages */}
              {seeMoreClicked && isExhausted && !loadingMore && (
                <div className="end-of-notifications">
                  <p>You're all caught up! 🎉</p>
                </div>
              )}

              {/* Sentinel div — IntersectionObserver watches this to trigger next fetch */}
              <div ref={sentinelRef} style={{ height: "1px" }} />
            </>
          )}
        </div>
      </section>

      {showDeletePopup && (
        <DeleteAllNotification
          notificationLength={notifications.length}
          setShowDeletePopup={setShowDeletePopup}
          handleClearAll={deleteAllNotification}
        />
      )}
    </>
  );
}